import { Component, inject, signal } from '@angular/core';
import { ICellRendererAngularComp } from 'ag-grid-angular';
import { MatDialog } from '@angular/material/dialog';
import { CommonModule, DatePipe } from '@angular/common';
import { MatCardModule } from '@angular/material/card';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTabsModule } from '@angular/material/tabs';
import { MatChipsModule } from '@angular/material/chips';
import { MatBadgeModule } from '@angular/material/badge';
import { MatDividerModule } from '@angular/material/divider';
import { MatTooltipModule } from '@angular/material/tooltip';
import { take } from 'rxjs';

import { Contract } from '../../../objects/contract';
import { Attachment } from '../../../objects/attachment';
import { DocumentViewerService } from '../../../service/document-viewer.service';
import { AttachmentsService } from '../../../service/files-and-attachments/attachments.service';
import { CriticalDatesService } from '../../../service/entity-services/critical-dates.service';
import { ContractService } from '../../../service/entity-services/contract.service';
import { AttachmentViewerComponent } from '../../../secure/entity-viewers/attachment-viewer/attachment-viewer.component';
import { VersionsComponent } from '../../../entity-form-components/versions/versions.component';
import { AttachmentConnectionsComponent } from '../../../entity-form-components/attachment-connections/attachment-connections.component';
import { environment } from '../../../../environments/environment';

const moment = require('moment');

/**
 * ContractDetailCellRendererComponent - "Sneak Peek" Detail Panel
 *
 * This component provides an expandable detail view within the contracts grid.
 * When a user clicks the expand arrow, this panel displays comprehensive contract information
 * including dates, alerts/warnings, attachments, and activity history.
 *
 * Features:
 * - Quick reference date information
 * - Critical dates grid with full date details
 * - Automated alerts and warnings based on compliance and expiration
 * - Attachment viewing and management
 * - Contract history/activity timeline (coming soon)
 */
@Component({
  selector: 'app-contract-detail-cell-renderer',
  templateUrl: './contract-detail-cell-renderer.component.html',
  styleUrls: ['./contract-detail-cell-renderer.component.css'],
  standalone: true,
  imports: [
    CommonModule,
    MatCardModule,
    MatButtonModule,
    MatIconModule,
    MatTabsModule,
    MatChipsModule,
    MatBadgeModule,
    MatDividerModule,
    MatTooltipModule,
    DatePipe,
    VersionsComponent,
    AttachmentConnectionsComponent
  ]
})
export class ContractDetailCellRendererComponent implements ICellRendererAngularComp {
  private dialog = inject(MatDialog);
  private documentViewerService = inject(DocumentViewerService);
  private attachmentsService = inject(AttachmentsService);
  private criticalDatesService = inject(CriticalDatesService);
  private contractService = inject(ContractService);

  // Core contract data
  selectedContract: Contract | null = null;

  // Data arrays for tables
  // Using any[] because API returns wrapped objects with criticalDateData property
  criticalDates = signal<any[]>([]);
  alertsAndWarnings = signal<any[]>([]);
  attachments = signal<Attachment[]>([]);
  contractHistory = signal<any[]>([]);

  // UI state
  overallStatus = signal<'Green' | 'Yellow' | 'Red'>('Green');
  viewContractDisabled = signal<boolean>(false);
  selectedTabIndex = signal<number>(0);

  /**
   * AG-Grid lifecycle method - called when cell renderer is initialized
   * Receives the contract data from the parent grid
   */
  async agInit(params: any): Promise<void> {
    const gridContract = params.data as Contract;

    if (!gridContract) {
      console.error('No contract data provided to detail renderer');
      return;
    }

    // Fetch fresh contract data from service instead of using stale grid data
    // This ensures we always have the latest attachments and critical dates
    try {
      this.contractService.visibleContracts$
        .pipe(take(1))
        .subscribe(contracts => {
          const freshContract = contracts.find(c => c.contractId === gridContract.contractId);

          if (freshContract) {
            this.selectedContract = freshContract;
          } else {
            this.selectedContract = gridContract;
          }

          this.viewContractDisabled.set(!this.hasViewableDocument(this.selectedContract));

          // Load all data with fresh contract
          this.loadCriticalDates();
          this.loadAttachments();
          this.calculateAlertsAndWarnings();

          // Contract History API coming soon - placeholder for now
          this.contractHistory.set([]);
        });
    } catch (error) {
      this.selectedContract = gridContract;

      this.viewContractDisabled.set(!this.hasViewableDocument(this.selectedContract));

      this.loadCriticalDates();
      this.loadAttachments();
      this.calculateAlertsAndWarnings();
      this.contractHistory.set([]);
    }
  }

  /**
   * Load critical dates associated with this contract
   * NEW API (Phase 1): Query by parentEntityId instead of array
   */
  async loadCriticalDates(): Promise<void> {
    if (!this.selectedContract?.contractId) {
      this.criticalDates.set([]);
      return;
    }

    try {
      this.criticalDatesService
        .getDatesByEntityId(this.selectedContract.contractId)
        .pipe(take(1))
        .subscribe({
          next: (dates) => {
            if (dates && dates.length > 0) {
              this.criticalDates.set(dates);
            } else if (this.selectedContract?.criticalDates?.length) {
              // Fallback: ETL'd contracts may not have parentEntityId on critical dates
              this.criticalDatesService.getDatesByNumbers(this.selectedContract.criticalDates)
                .pipe(take(1))
                .subscribe(legacyDates => {
                  this.criticalDates.set(legacyDates || []);
                });
            } else {
              this.criticalDates.set([]);
            }
          },
          error: () => {
            this.criticalDates.set([]);
          }
        });
    } catch (error) {
      this.criticalDates.set([]);
    }
  }

  /**
   * Load attachments associated with this contract
   * NEW API (Phase 1): Query by parentEntityId instead of array
   */
  async loadAttachments(): Promise<void> {
    if (!this.selectedContract?.contractId) {
      this.attachments.set([]);
      return;
    }

    try {
      this.attachmentsService
        .getAttachmentsForEntity(this.selectedContract.contractId, 'Contract')
        .pipe(take(1))
        .subscribe({
          next: (attachments) => {
            if (attachments && attachments.length > 0) {
              this.attachments.set(attachments);
            } else if (this.selectedContract?.attachments?.length) {
              // Fallback: ETL'd contracts may not have parentEntityId on attachments
              this.attachmentsService.getAttachmentsByNumbers(this.selectedContract.attachments)
                .pipe(take(1))
                .subscribe(legacyAttachments => {
                  this.attachments.set(legacyAttachments || []);
                });
            } else {
              this.attachments.set([]);
            }
          },
          error: () => {
            this.attachments.set([]);
          }
        });
    } catch (error) {
      this.attachments.set([]);
    }
  }

  /**
   * Calculate alerts and warnings based on contract compliance and dates
   *
   * Logic:
   * - Yellow Warning: Missing BAA, COI, signature, or PO
   * - Yellow Warning: Expiration date within 180 days
   * - Red Alert: Contract is expired
   *
   * Overall status is the most severe condition found
   */
  calculateAlertsAndWarnings(): void {
    if (!this.selectedContract) return;

    const alerts: any[] = [];

    // Check for missing compliance items
    if (this.selectedContract.baaRequired) {
      alerts.push({
        classification: 'Warning',
        name: 'Missing BAA',
        date: null
      });
      this.updateOverallStatus('Yellow');
    }

    if (this.selectedContract.coiRequired) {
      alerts.push({
        classification: 'Warning',
        name: 'Missing COI',
        date: null
      });
      this.updateOverallStatus('Yellow');
    }

    if (this.selectedContract.missingSignature) {
      alerts.push({
        classification: 'Warning',
        name: 'Missing Signature',
        date: null
      });
      this.updateOverallStatus('Yellow');
    }

    if (this.selectedContract.poRequired) {
      alerts.push({
        classification: 'Warning',
        name: 'Missing PO',
        date: null
      });
      this.updateOverallStatus('Yellow');
    }

    // Check expiration date
    if (this.selectedContract.expirationDate) {
      const dateToEvaluate = moment(this.selectedContract.expirationDate);

      if (dateToEvaluate.isValid()) {
        const daysPast = moment().diff(dateToEvaluate, 'days');

        if (daysPast >= 0) {
          // Contract is expired - RED ALERT
          this.updateOverallStatus('Red');
          alerts.push({
            classification: 'Alert',
            name: 'Contract is expired',
            date: dateToEvaluate.format('MM/DD/YYYY')
          });
        } else if (daysPast < 0 && Math.abs(daysPast) <= 180) {
          // Expiration approaching within 180 days - YELLOW WARNING
          this.updateOverallStatus('Yellow');
          alerts.push({
            classification: 'Warning',
            name: 'Expiration Date Approaching',
            date: dateToEvaluate.format('MM/DD/YYYY')
          });
        }
      }
    }

    this.alertsAndWarnings.set(alerts);
  }

  /**
   * Update the overall status indicator
   * Priority: Red > Yellow > Green
   */
  updateOverallStatus(status: 'Green' | 'Yellow' | 'Red'): void {
    const currentStatus = this.overallStatus();

    // Red overrides everything
    if (currentStatus === 'Red') return;

    // Yellow overrides green
    if (currentStatus === 'Yellow' && status === 'Green') return;

    this.overallStatus.set(status);
  }


  /**
   * Resolve the contract's document URL, mirroring contract-form's priority:
   * 1. contractFileName / contractFile — bare filename, prepend S3 location
   * 2. linkToContract — only when it's a real S3 URL (ETL'd contracts)
   * Returns null when no viewable document is attached.
   */
  private resolveDocumentUrl(contract: Contract | null): string | null {
    if (!contract) return null;

    const fileName = contract.contractFileName || contract.contractFile;
    if (fileName) return environment.s3Location + fileName;

    const link = contract.linkToContract;
    if (link && typeof link === 'string'
        && link.includes('.s3.') && link.includes('amazonaws.com')) {
      return link;
    }

    return null;
  }

  private hasViewableDocument(contract: Contract | null): boolean {
    return this.resolveDocumentUrl(contract) !== null;
  }

  /**
   * Open the contract document viewer
   * Uses the same document viewing infrastructure as contract-form.component
   */
  async viewContractDialog(): Promise<void> {
    const url = this.resolveDocumentUrl(this.selectedContract);
    if (!url) {
      console.warn('No contract document available to view');
      return;
    }

    const documentResult = await this.documentViewerService.processDocument(url);
    if (documentResult?.fetchUrl) {
      // fetchUrl, not directLink — the bucket is private, so only a presigned url resolves.
      this.documentViewerService.openInNewTab(documentResult.fetchUrl);
    }
  }

  /**
   * Handle attachment cell click - open attachment viewer
   * Supports both flat attachment objects (new API) and wrapped objects (legacy)
   */
  onAttachmentCellClicked(event: any): void {
    // Handle both flat and wrapped attachment formats
    const attachment = event.data?.attachmentData || event.data;
    if (!attachment) return;

    // Open attachment viewer dialog
    const dialogRef = this.dialog.open(AttachmentViewerComponent, {
      width: '90vw',
      height: '90vh',
      maxWidth: '1200px',
      data: { attachment }
    });

    dialogRef.afterClosed().subscribe(() => {
      // Handle any post-view actions if needed
    });
  }

  /**
   * View attachment from simple table
   * Called directly with flat attachment object from the template
   */
  viewAttachment(attachment: any): void {
    if (!attachment) return;

    // Open attachment viewer dialog directly with the flat attachment
    const dialogRef = this.dialog.open(AttachmentViewerComponent, {
      width: '90vw',
      height: '90vh',
      maxWidth: '1200px',
      data: { attachment }
    });

    dialogRef.afterClosed().subscribe(() => {
      // Handle any post-view actions if needed
    });
  }

  /**
   * AG-Grid required method for cell renderer refresh
   */
  refresh(params: any): boolean {
    return false;
  }

  // ============================================================================
  // Future API Integration - Contract History
  // ============================================================================

  /**
   * TODO: Load contract history/activity timeline
   *
   * This feature requires a new API endpoint that returns the contract's edit history.
   *
   * Expected API:
   * - Endpoint: GET /api/contracts/{contractId}/history
   * - Response: Array of history objects with:
   *   - user: string (who made the change)
   *   - action: string (what was changed)
   *   - timestamp: number (Unix timestamp)
   *   - details: object (optional - specific field changes)
   *
   * Implementation:
   * 1. Create ContractService.getContractHistory(contractId: string)
   * 2. Wire up this method to call the API
   * 3. Update this.contractHistory signal with the response
   * 4. Remove the "Coming Soon" placeholder from the Activity tab
   *
   * @param _contractId - The contract's unique identifier (prefixed with _ to indicate unused)
   */
  async loadContractHistory(_contractId: string): Promise<void> {
    // Placeholder implementation
    // Once API is ready, uncomment and implement:

    // try {
    //   const history = await this.contractService.getContractHistory(_contractId);
    //   this.contractHistory.set(history || []);
    //
    //   if (this.contractHistoryGridApi) {
    //     this.contractHistoryGridApi.sizeColumnsToFit();
    //   }
    // } catch (error) {
    //   console.error('Error loading contract history:', error);
    //   this.contractHistory.set([]);
    // }
  }
}
